import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { veiculosApi } from '../../api/endpoints'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/EmptyState'
import { PageHeader } from '../../layout/PageHeader'
import { StatusVeiculo } from '../../lib/enums'
import { formatMoney } from '../../lib/utils'
import { BloqueioRecurso } from '../plano/BloqueioRecurso'
import { useRecurso } from '../plano/useRecurso'
import type { VeiculoResumo } from '../../types'
import { VeiculoFormModal } from './VeiculoFormModal'
import { energiaLabel, formatKm, iconeVeiculo, mesReferencia, numeroBr, unidadeConsumo } from './veiculoUtils'

/** Garagem: os veículos ativos, os vendidos e o limite do plano. */
export function VeiculosPage() {
  const navigate = useNavigate()
  const modulo = useRecurso('veiculos.modulo')
  const max = useRecurso('veiculos.max')
  const eletrico = useRecurso('veiculos.tipo.eletrico')
  const hibrido = useRecurso('veiculos.tipo.hibrido')
  const [criando, setCriando] = useState(false)

  const { data: veiculos = [], isLoading, isError, error } = useQuery({
    queryKey: ['veiculos'], queryFn: veiculosApi.list, enabled: modulo.habilitado,
  })

  if (!modulo.habilitado) {
    return (
      <>
        <PageHeader title="Veículos" />
        <BloqueioRecurso mensagem="O módulo de veículos não está incluído no seu plano." />
      </>
    )
  }

  // O limite conta só os meus veículos ativos.
  const ativos = veiculos.filter((v) => v.status === StatusVeiculo.Ativo)
  const vendidos = veiculos.filter((v) => v.status === StatusVeiculo.Vendido)
  const meusAtivos = ativos.filter((v) => !v.compartilhado).length
  const limiteAtingido = max.limite !== null && meusAtivos >= max.limite
  const planoLimita = !eletrico.habilitado || !hibrido.habilitado

  return (
    <>
      <PageHeader title="Veículos" />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-13 text-muted">
          {max.limite === null
            ? <><span className="font-medium text-white">{meusAtivos}</span> veículos (sem limite no seu plano)</>
            : <><span className="font-medium text-white">{meusAtivos} de {max.limite}</span> veículos no seu plano</>}
        </p>
        {limiteAtingido ? (
          <Link to="/plano" className="inline-flex h-[38px] items-center gap-2 rounded bg-primary/10 px-4 text-13 font-medium text-primary hover:bg-primary hover:text-bg">
            <i className="ri-lock-line" aria-hidden /> Limite atingido, ver planos
          </Link>
        ) : (
          <Button data-tutorial="garagem.novo" icon="ri-add-line" onClick={() => setCriando(true)}>Novo veículo</Button>
        )}
      </div>

      {planoLimita && (
        <div className="mb-4 flex items-center gap-2 rounded bg-info/10 px-4 py-2.5 text-13 text-info">
          <i className="ri-lock-line" aria-hidden />
          <span>
            {!eletrico.habilitado && !hibrido.habilitado ? 'Veículos elétricos e híbridos' : !eletrico.habilitado ? 'Veículos elétricos' : 'Veículos híbridos'} não estão incluídos no seu plano.{' '}
            <Link to="/plano" className="underline underline-offset-2">Ver planos</Link>
          </span>
        </div>
      )}

      {isLoading ? <Card><LoadingState /></Card> : isError ? <Card><ErrorState message={(error as Error)?.message || 'Não foi possível carregar os veículos.'} /></Card> : veiculos.length === 0 ? (
        <Card>
          <EmptyState icon="ri-car-line" message="Você ainda não cadastrou nenhum veículo." action={<Button variant="soft-primary" icon="ri-add-line" onClick={() => setCriando(true)}>Cadastrar o primeiro</Button>} />
        </Card>
      ) : (
        <>
          {ativos.length > 0 && <Grade veiculos={ativos} />}
          {vendidos.length > 0 && (
            <section className="mt-8">
              <h5 className="mb-3 text-13 font-medium uppercase text-muted">Vendidos</h5>
              <Grade veiculos={vendidos} />
            </section>
          )}
        </>
      )}

      <VeiculoFormModal open={criando} onClose={() => setCriando(false)} onSalvo={(v) => { setCriando(false); navigate(`/veiculos/${v.id}`) }} />
    </>
  )
}

function Grade({ veiculos }: { veiculos: VeiculoResumo[] }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {veiculos.map((v) => <CardVeiculo key={v.id} v={v} />)}
    </div>
  )
}

function CardVeiculo({ v }: { v: VeiculoResumo }) {
  const vendido = v.status === StatusVeiculo.Vendido
  const abastecimentoOk = useRecurso('veiculos.abastecimento').habilitado
  const { data: consumo } = useQuery({ queryKey: ['veiculos', v.id, 'consumo', '', ''], queryFn: () => veiculosApi.consumo(v.id), enabled: abastecimentoOk, staleTime: 60_000 })
  const energia = consumo?.energias.find((e) => e.mediaPonderada != null)
  return (
    <Link to={`/veiculos/${v.id}`} className="block rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
      <Card className={`h-full p-4 transition-colors hover:bg-surface2 ${vendido ? 'opacity-70' : ''}`}>
        <div className="flex items-start gap-3">
          <span className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded bg-surface3 text-2xl text-white" style={v.corHex ? { color: v.corHex } : undefined}>
            <i className={iconeVeiculo(v.tipo, v.tipoEnergia)} aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <h4 className="truncate text-base font-semibold text-white">{v.apelido}</h4>
            <p className="truncate text-13 text-muted">{v.marca} {v.modelo} · {v.anoModelo}</p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Badge variant="muted">{energiaLabel(v.tipoEnergia)}</Badge>
          {v.corNome && <Badge variant="muted" className="normal-case">{v.corNome}</Badge>}
          {v.placa && <Badge variant="muted">{v.placa}</Badge>}
          {vendido && <Badge variant="warning">Vendido</Badge>}
          {v.compartilhado && <Badge variant="info" className="normal-case"><i className="ri-user-shared-line" aria-hidden /> {v.donoNome.split(' ')[0]}</Badge>}
        </div>
        {(energia || consumo?.custoPorKm != null) && (
          <div className="mt-3 flex gap-4 text-13">
            {energia && <span className="text-white"><i className="ri-speed-line text-muted" aria-hidden /> {numeroBr(energia.mediaPonderada!, 1)} {unidadeConsumo(energia.unidade)}</span>}
            {consumo?.custoPorKm != null && <span className="text-white"><i className="ri-coin-line text-muted" aria-hidden /> {formatMoney(consumo.custoPorKm)}/km</span>}
          </div>
        )}
        <div className="mt-4 flex items-end justify-between gap-3 border-t border-border/70 pt-3">
          <div>
            <p className="text-2xs uppercase text-muted">Quilometragem</p>
            <p className="text-15 font-medium text-white">{formatKm(v.kmAtual)}</p>
          </div>
          {v.valorFipe && (
            <div className="text-right" title="Tabela FIPE, não é avaliação oficial">
              <p className="text-2xs uppercase text-muted">FIPE {mesReferencia(v.valorFipe.mesReferencia)}</p>
              <p className="text-15 font-medium text-primary">{formatMoney(v.valorFipe.valor)}</p>
            </div>
          )}
        </div>
      </Card>
    </Link>
  )
}
